import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Brain } from 'lucide-react'

interface Etapa6Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

export function Etapa6CognicaoComportamento({ formData, onChange, onNext, onPrev, onSubmit, saving, submitting }: Etapa6Props) {
  const cognicao = formData.cognicao || {}

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    // Validar declaração de veracidade
    if (!formData.veracidade_etapa6) {
      errors.push('Declaração de veracidade')
    }

    // Validar memória e reconhecimento
    if (!cognicao.reconhece_familiares && !cognicao.esquece_eventos && !cognicao.memoria_reconhecimento_ok) {
      errors.push('Selecione pelo menos uma opção em Memória e Reconhecimento')
    }

    // Validar comportamento
    if (!cognicao.agressividade && !cognicao.agitacao && !cognicao.fuga && !cognicao.comportamento_normal) {
      errors.push('Selecione pelo menos uma opção em Comportamento')
    }

    // Validar condições neurológicas
    if (!cognicao.alucinacoes && !cognicao.demencia && !cognicao.alzheimer && !cognicao.condicoes_neurologicas_ok) {
      errors.push('Selecione pelo menos uma opção em Condições Neurológicas')
    }

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  const handleCognicaoChange = (campo: string, valor: boolean) => {
    onChange('cognicao', {
      ...cognicao,
      [campo]: valor
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Cognição e Comportamento
          </CardTitle>
          <p className="text-sm text-gray-600">
            Avalie os aspectos cognitivos e comportamentais do idoso(a).
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold">Memória e Reconhecimento</h4>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reconhece_familiares"
                  checked={cognicao.reconhece_familiares || false}
                  onCheckedChange={(checked) => handleCognicaoChange('reconhece_familiares', checked as boolean)}
                />
                <Label htmlFor="reconhece_familiares">
                  Reconhece familiares
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="esquece_eventos"
                  checked={cognicao.esquece_eventos || false}
                  onCheckedChange={(checked) => handleCognicaoChange('esquece_eventos', checked as boolean)}
                />
                <Label htmlFor="esquece_eventos">
                  Esquece eventos recentes
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="memoria_reconhecimento_ok"
                  checked={cognicao.memoria_reconhecimento_ok || false}
                  onCheckedChange={(checked) => handleCognicaoChange('memoria_reconhecimento_ok', checked as boolean)}
                />
                <Label htmlFor="memoria_reconhecimento_ok">
                  Memória e reconhecimento normais
                </Label>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold">Comportamento</h4>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="agressividade"
                  checked={cognicao.agressividade || false}
                  onCheckedChange={(checked) => handleCognicaoChange('agressividade', checked as boolean)}
                />
                <Label htmlFor="agressividade">
                  Apresenta agressividade
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="agitacao"
                  checked={cognicao.agitacao || false}
                  onCheckedChange={(checked) => handleCognicaoChange('agitacao', checked as boolean)}
                />
                <Label htmlFor="agitacao">
                  Apresenta agitação
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fuga"
                  checked={cognicao.fuga || false}
                  onCheckedChange={(checked) => handleCognicaoChange('fuga', checked as boolean)}
                />
                <Label htmlFor="fuga">
                  Tende a fugir/errar
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="comportamento_normal"
                  checked={cognicao.comportamento_normal || false}
                  onCheckedChange={(checked) => handleCognicaoChange('comportamento_normal', checked as boolean)}
                />
                <Label htmlFor="comportamento_normal">
                  Comportamento normal
                </Label>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold">Condições Neurológicas</h4>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="alucinacoes"
                  checked={cognicao.alucinacoes || false}
                  onCheckedChange={(checked) => handleCognicaoChange('alucinacoes', checked as boolean)}
                />
                <Label htmlFor="alucinacoes">
                  Apresenta alucinações
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="demencia"
                  checked={cognicao.demencia || false}
                  onCheckedChange={(checked) => handleCognicaoChange('demencia', checked as boolean)}
                />
                <Label htmlFor="demencia">
                  Diagnóstico de demência
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="alzheimer"
                  checked={cognicao.alzheimer || false}
                  onCheckedChange={(checked) => handleCognicaoChange('alzheimer', checked as boolean)}
                />
                <Label htmlFor="alzheimer">
                  Diagnóstico de Alzheimer
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="condicoes_neurologicas_ok"
                  checked={cognicao.condicoes_neurologicas_ok || false}
                  onCheckedChange={(checked) => handleCognicaoChange('condicoes_neurologicas_ok', checked as boolean)}
                />
                <Label htmlFor="condicoes_neurologicas_ok">
                  Condições neurológicas normais
                </Label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="observacoes_cognicao">Observações adicionais</Label>
            <Textarea
              id="observacoes_cognicao"
              value={cognicao.observacoes || ''}
              onChange={(e) => onChange('cognicao', {
                ...cognicao,
                observacoes: e.target.value
              })}
              placeholder="Descreva outros aspectos cognitivos ou comportamentais relevantes"
              className="mt-1"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termo de Veracidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Declaro que as informações prestadas nesta avaliação funcional são verdadeiras e 
              foram fornecidas com base no meu conhecimento sobre a capacidade do idoso(a).
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="veracidade_etapa6"
                checked={formData.veracidade_etapa6 || false}
                onCheckedChange={(checked) => onChange('veracidade_etapa6', checked as boolean)}
                className="!rounded-full !data-[state=checked]:bg-green-600 !data-[state=checked]:border-green-600"
              />
              <Label htmlFor="veracidade_etapa6" className="text-sm font-medium">
                Declaro que as informações são verdadeiras *
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
